import React, { type FC, useCallback } from 'react';
import { useTranslation, useEnvironment } from '@wix/yoshi-flow-editor';
import {
  ButtonPriority,
  Dialog,
  TextTypography,
  type DialogProps,
} from 'wix-ui-tpa/cssVars';
import { BiModalCloseTrigger } from '@services/BiLogger';
import { Button, type ButtonProps } from '@components/Button';
import { Text } from '@components/Text';
import { useScrollControl } from './ModalContext';
import { DIALOG_DESCRIPTION_ID, DIALOG_TITLE_ID } from './constants';
import { DataHooks } from './DataHooks';
import { st, classes } from './AlertDialog.st.css';

type AlertModalProps = Omit<DialogProps, 'onClose'> & {
  title: string;
  isSubmitting?: boolean;
  cancelText: string;
  submitText: string;
  submitIcon?: ButtonProps['prefixIcon'];
  onSubmit: () => void;
  onClose?: (action: BiModalCloseTrigger) => void;
};

export const AlertDialog: FC<AlertModalProps> = (props) => {
  const {
    title,
    isOpen,
    isSubmitting,
    children,
    cancelText,
    submitText,
    submitIcon,
    onSubmit,
    onClose,
  } = props;
  const { isMobile } = useEnvironment();
  const { t } = useTranslation();

  useScrollControl(isOpen);

  const createCloseHandler = useCallback(
    (action: BiModalCloseTrigger) => () => {
      onClose?.(action);
    },
    [onClose],
  );

  return (
    <Dialog
      notFullscreenOnMobile
      wiredToSiteColors
      isOpen={isOpen}
      className={st(classes.root, { isMobile })}
      contentClassName={st(classes.content)}
      data-hook={DataHooks.Root}
      aria-labelledby={DIALOG_TITLE_ID}
      aria-describedby={DIALOG_DESCRIPTION_ID}
      closeButtonAriaLabel={t('app.modal.close-button.aria-label')}
      onClose={createCloseHandler(BiModalCloseTrigger.ClickXButton)}
    >
      <Text
        id={DIALOG_TITLE_ID}
        tagName="h2"
        typography={
          isMobile ? TextTypography.smallTitle : TextTypography.largeTitle
        }
        data-hook={DataHooks.Title}
        className={st(classes.title)}
      >
        {title}
      </Text>
      <Text
        id={DIALOG_DESCRIPTION_ID}
        tagName="p"
        data-hook={DataHooks.Description}
      >
        {children}
      </Text>
      <div className={st(classes.actions)}>
        <Button
          autoFocus
          isLoading={isSubmitting}
          prefixIcon={submitIcon}
          data-hook={DataHooks.SubmitButton}
          onClick={onSubmit}
        >
          {submitText}
        </Button>
        <Button
          priority={ButtonPriority.basicSecondary}
          data-hook={DataHooks.CancelButton}
          onClick={createCloseHandler(BiModalCloseTrigger.ClickCancelButton)}
        >
          {cancelText}
        </Button>
      </div>
    </Dialog>
  );
};
